import { PicoBubble } from "../../../components/PicoBubble";
import { useI18n } from "../../../i18n";

/**
 * 놀이터 무대 공용 조각(2026-10-08 — 스마트브릭 기구 무대 · micro:bit 놀이터가 같은 모습) — 별 3개 · 목표 줄 · 소리 단추 · 피코 한마디 · 결과 별.
 * 모양 · 시험 열쇠(data-testid)는 기구 무대(FS-SB)의 것 그대로다. 놀이터마다 다른 것(판정 · 입력)은 부르는 화면이 정한다.
 */

/** 무대 위 별 3개(지금 몇 개) */
export function StageStars({ stars, testId = "stage-stars" }: { stars: number; testId?: string }) {
  const { t } = useI18n();
  return (
    <div
      className="pf-stage__stars"
      data-testid={testId}
      aria-label={t("stage.hud.stars", { n: stars })}
    >
      {[1, 2, 3].map((i) => (
        <span key={i} className={`pf-stage__star${i <= stars ? " is-on" : ""}`} aria-hidden="true">
          ★
        </span>
      ))}
    </div>
  );
}

export interface StageGoalLine {
  readonly text: string;
  readonly done: boolean;
  /** 0~1 진행 막대(없으면 막대 없음) */
  readonly fill?: number;
  /** 오른쪽 수(3/5 회) */
  readonly num?: string;
}

/** 무대 위 목표 줄(⭐ · ⭐⭐ · ⭐⭐⭐) · 보너스를 찾았으면 🎁 */
export function StageGoalList({
  goals,
  bonusDone = false,
  testId = "stage-goals",
}: {
  goals: readonly StageGoalLine[];
  bonusDone?: boolean;
  testId?: string;
}) {
  const { t } = useI18n();
  return (
    <ol className="pf-stage__goals" data-testid={testId}>
      {goals.map((g, i) => (
        <li key={i} className={`pf-stage__goal${g.done ? " is-done" : ""}`} data-done={g.done}>
          <span className="pf-stage__goal-star" aria-hidden="true">
            {"⭐".repeat(i + 1)}
          </span>
          <span className="pf-stage__goal-text">{g.text}</span>
          {g.fill !== undefined && (
            <span className="pf-stage__bar" aria-hidden="true">
              <span style={{ width: `${Math.round(Math.max(0, Math.min(1, g.fill)) * 100)}%` }} />
            </span>
          )}
          {g.num && <span className="pf-stage__goal-num">{g.num}</span>}
        </li>
      ))}
      {bonusDone && (
        <li className="pf-stage__goal pf-stage__goal--bonus is-done" data-testid="stage-bonus">
          🎁 {t("stage.bonusFound")}
        </li>
      )}
    </ol>
  );
}

/** 소리 켜기 · 끄기(무대 오른쪽 위) */
export function StageSoundButton({ muted, onToggle }: { muted: boolean; onToggle: () => void }) {
  const { t } = useI18n();
  return (
    <button
      type="button"
      className="pf-stage__sound"
      aria-label={t(muted ? "stage.hud.unmute" : "stage.hud.mute")}
      data-testid="stage-sound"
      onClick={onToggle}
    >
      {muted ? "🔇" : "🔊"}
    </button>
  );
}

/** 피코 한마디(잠깐 — 지우기는 부르는 쪽) */
export function StagePico({ line }: { line: string | null }) {
  if (!line) return null;
  return (
    <div className="pf-stage__pico" role="status" data-testid="stage-pico">
      <PicoBubble text={line} live={false} />
    </div>
  );
}

/** 결과 머리 — 큰 별 3개 · 별 수에 맞는 피코 말 · 보너스 */
export function StageResultHead({ stars, bonus = false }: { stars: number; bonus?: boolean }) {
  const { t } = useI18n();
  return (
    <>
      <div className="pf-stage__result-stars" aria-hidden="true">
        {[1, 2, 3].map((i) => (
          <span
            key={i}
            className={`pf-stage__star pf-stage__star--big${i <= stars ? " is-on" : ""}`}
          >
            ★
          </span>
        ))}
      </div>
      <PicoBubble text={t(`stage.result.stars${stars}`)} live />
      {bonus && <p className="pf-stage__bonus">🎁 {t("stage.bonusFound")}</p>}
    </>
  );
}
